/**
 * SelectionFloatingBar
 *
 * Floating action bar that appears at the bottom of the table when rows
 * are selected. Contains "Set field" bulk action, archive/restore, and
 * handles Ctrl+D fill-down.
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Archive, RotateCcw, Loader2 } from 'lucide-react';
import type { Table as TanstackTable } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { InlineEditingContext } from './types';
import { BulkSetFieldAction } from './BulkSetFieldAction';
import { useBulkSetField, buildRowsForBulkSet } from './useBulkSetField';

interface SelectionFloatingBarProps<T> {
  selectedCount: number;
  inlineEditing?: InlineEditingContext;
  table: TanstackTable<T>;
  doctype: string;
  getRowId: (row: T) => string;
  onClearSelection: () => void;
  onArchive?: () => Promise<void>;
  onRestore?: () => Promise<void>;
  showRestore?: boolean;
}

export function SelectionFloatingBar<T>({
  selectedCount,
  inlineEditing,
  table,
  doctype,
  getRowId,
  onClearSelection,
  onArchive,
  onRestore,
  showRestore,
}: SelectionFloatingBarProps<T>) {
  const { bulkSet } = useBulkSetField(doctype);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  // Handle "Set field" apply from BulkSetFieldAction
  const handleApply = useCallback(
    (fieldname: string, value: unknown) => {
      const selectedRows = table.getSelectedRowModel().rows;
      const rows = buildRowsForBulkSet(selectedRows, getRowId, fieldname);
      bulkSet(rows, fieldname, value);
    },
    [table, getRowId, bulkSet],
  );

  const handleArchiveConfirm = useCallback(async () => {
    if (!onArchive) return;
    setArchiveOpen(false);
    setLoadingAction('archive');
    try {
      await onArchive();
    } finally {
      setLoadingAction(null);
    }
  }, [onArchive]);

  const handleRestore = useCallback(async () => {
    if (!onRestore) return;
    setLoadingAction('restore');
    try {
      await onRestore();
    } finally {
      setLoadingAction(null);
    }
  }, [onRestore]);

  const isLoading = loadingAction !== null;

  // Fill-down keyboard handler: Ctrl+D / Cmd+D
  useEffect(() => {
    if (!inlineEditing) return;

    const handler = (e: KeyboardEvent) => {
      if (!((e.ctrlKey || e.metaKey) && e.key === 'd')) return;

      const { lastEditedColumn } = inlineEditing;
      if (!lastEditedColumn || selectedCount <= 1) return;

      // Prevent browser bookmark dialog
      e.preventDefault();

      const selectedRows = table.getSelectedRowModel().rows;
      if (selectedRows.length <= 1) return;

      // Source value from first selected row
      const firstRow = selectedRows[0];
      const sourceValue = (firstRow.original as Record<string, unknown>)[lastEditedColumn];

      // Build rows for remaining rows (exclude first)
      const remainingRows = selectedRows.slice(1);
      const rows = buildRowsForBulkSet(remainingRows, getRowId, lastEditedColumn);

      // Apply immediately (no confirmation — spreadsheet convention)
      bulkSet(rows, lastEditedColumn, sourceValue);
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [inlineEditing, selectedCount, table, getRowId, bulkSet]);

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-background border shadow-lg rounded-lg px-4 py-2.5">
        {/* Selection count */}
        <span className="text-sm font-medium tabular-nums">
          {selectedCount} selected
        </span>

        <div className="w-px h-5 bg-border" />

        {/* Bulk set field action (only when inline editing is active) */}
        {inlineEditing && (
          <>
            <BulkSetFieldAction
              editableFields={inlineEditing.editableFields}
              fieldMetaMap={inlineEditing.fieldMetaMap}
              selectedRowCount={selectedCount}
              onApply={handleApply}
            />
            <div className="w-px h-5 bg-border" />
          </>
        )}

        {/* Restore (shown when viewing archived items) */}
        {showRestore && onRestore && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            disabled={isLoading}
            onClick={handleRestore}
          >
            {loadingAction === 'restore' && (
              <Loader2 className="h-3 w-3 animate-spin" />
            )}
            <RotateCcw className="h-3 w-3" />
            Restore
          </Button>
        )}

        {/* Archive (hidden when viewing archived items) */}
        {!showRestore && onArchive && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            disabled={isLoading}
            onClick={() => setArchiveOpen(true)}
          >
            {loadingAction === 'archive' && (
              <Loader2 className="h-3 w-3 animate-spin" />
            )}
            <Archive className="h-3 w-3" />
            Archive
          </Button>
        )}

        {/* Clear selection */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5"
          onClick={onClearSelection}
          disabled={isLoading}
        >
          <X className="h-3.5 w-3.5" />
          Clear
        </Button>
      </div>

      {/* Archive confirmation dialog */}
      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Archive {selectedCount} {selectedCount === 1 ? 'item' : 'items'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They will be hidden from active lists but can be restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveConfirm}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
